import CaretLoader from '@root/core/src/components/caret-loader';
import MediaAlphaScriptService from '@root/core/src/services/media-alpha-script-service';
import PropTypes from '@root/vendor/prop-types';
import Responsive from '@root/core/src/utils/responsive';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import { StyleSheet, Theme } from '@root/core/src/utils/styles';
import { useEffect } from '@root/vendor/react';

export default function MediaAlphaResell({
  analyticsContext,
  explainerText,
  heading = 'We want to help you find the best option for your needs.',
  placementId,
  request,
  shouldTrackViewEvent = true,
  shouldUseFlushPositioning = false,
}) {
  const { trackEvent } = useAnalytics(analyticsContext, shouldTrackViewEvent);

  useEffect(() => {
    MediaAlphaScriptService.createScript(placementId, 'media_alpha_element', request);

    trackEvent('QUOTES_LOADED');

    return () => {
      MediaAlphaScriptService.cleanupScript();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // We intentionally do not want this effect to run on re-renders. It should only run once on mount

  return (
    <div css={[styles.container, shouldUseFlushPositioning && styles.containerFlush]}>
      {heading &&
        <h2 css={[styles.heading, shouldUseFlushPositioning && styles.flushHorizontal]}>
          {heading}
        </h2>
      }
      {explainerText &&
        <p css={[styles.subtext, shouldUseFlushPositioning && styles.flushHorizontal]}>
          {explainerText}
        </p>
      }
      <div
        css={styles.quotesContainer}
        data-testid={'media-alpha-container'}
        id={'media_alpha_element'}
      >
        <CaretLoader />
      </div>
      <p css={[styles.disclaimer, shouldUseFlushPositioning && styles.flushHorizontal]}>
        {'Root is partnered with MediaAlpha, a shopping center for insurance, to recommend carrier options. By clicking on an option above, you’ll be leaving Root and will be directed to MediaAlpha. We will pass along your information to MediaAlpha, who can help generate insurance recommendations for you.'}
      </p>
    </div>
  );
}

MediaAlphaResell.propTypes = {
  analyticsContext: PropTypes.string.isRequired,
  explainerText: PropTypes.string,
  heading: PropTypes.string,
  placementId: PropTypes.string.isRequired,
  request: PropTypes.object.isRequired,
  // Set to false when a parent already tracks the view event for this
  // analyticsContext, to avoid emitting a duplicate VIEWED event.
  shouldTrackViewEvent: PropTypes.bool,
  shouldUseFlushPositioning: PropTypes.bool,
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 770,
    padding: '0 5px',
    ...Responsive.sm({
      width: '95%',
      margin: '0 auto',
      padding: 0,
    }),
  },
  heading: {
    ...Theme.heading2(),
    ...Responsive.sm({
      padding: '0 20px',
    }),
  },
  subtext: {
    ...Theme.paragraph1(),
    marginTop: 20,
    ...Responsive.sm({
      padding: '0 20px',
    }),
  },
  quotesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 30,
  },
  disclaimer: {
    ...Theme.paragraph2(),
    ...Responsive.sm({
      padding: '0 20px',
    }),
  },
  containerFlush: {
    maxWidth: 'none',
    padding: 0,
    ...Responsive.sm({
      width: '100%',
      margin: 0,
      padding: 0,
    }),
  },
  flushHorizontal: {
    ...Responsive.sm({
      padding: 0,
    }),
  },
});
